/* eslint-disable @angular-eslint/prefer-inject */
import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import { AuditsService } from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { AuditStore } from './audit.store';
import {
    AuditCreate,
    AuditRead,
    AuditSearchParams,
} from '../../entities/audit.dto';
import { Operation } from 'fast-json-patch';

@Injectable()
export class AuditDataService extends AbstractDataService<
    AuditRead,
    AuditCreate
> {
    protected EntityName = PFWmsEntities.Audit;
    constructor(
        private service: AuditsService,
        store: AuditStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: AuditCreate): Observable<AuditRead> {
        return this.service.auditCreate({
            auditViewModelCreateBody: body,
        }) as Observable<AuditRead>;
    }
    protected deleteObs$(id: string): Observable<AuditRead> {
        return this.service.auditDelete({
            id,
        }) as Observable<AuditRead>;
    }
    protected undoDeleteObs$(id: string): Observable<AuditRead> {
        return this.service.auditUndoDelete({
            id,
        }) as Observable<AuditRead>;
    }
    protected getObs$(id: string): Observable<AuditRead> {
        return this.service.auditReadById({
            id,
        }) as Observable<AuditRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<AuditRead> {
        return this.service.auditUpdatePatch({
            id,
            operation: operations,
        }) as Observable<AuditRead>;
    }
    protected searchObs$(
        searchParams: AuditSearchParams
    ): Observable<IPagedResult<AuditRead>> {
        return this.service.auditSearch(searchParams) as Observable<
            IPagedResult<AuditRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<AuditRead>>> {
        return this.service.auditGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<AuditRead>>
        >;
    }

    claimAudits$(warehouseId: string, auditIds: string[]) {
        return this.service
            .v1AuditsClaimPost({
                auditClaimRequest: {
                    auditIds,
                    warehouseId,
                },
            })
            .pipe(
                map(claimedAudits => claimedAudits as AuditRead[]),
                tap(claimedAudits => {
                    this.store.upsertMany(claimedAudits);
                })
            );
    }

    releaseAudits$(warehouseId: string, auditIds: string[]) {
        return this.service
            .v1AuditsUnclaimPost({
                auditUnclaimRequest: {
                    auditIds,
                    warehouseId,
                },
            })
            .pipe(
                map(unclaimedAudits => unclaimedAudits as AuditRead[]),
                tap(unclaimedAudits => {
                    this.store.upsertMany(unclaimedAudits);
                })
            );
    }
}
